<template>
  <v-container>
    <v-card>
      <v-row align="center" class="py-1 px-2" no-gutters>
        <v-col cols="12" sm="6">
          <v-breadcrumbs class="px-2" :items="breadcrumbsItems"></v-breadcrumbs>
        </v-col>
        <v-col
          cols="12"
          sm="6"
          :class="$store.getters.LANGUAGE == 'fa' ? 'text-left' : 'text-right'"
        >
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                fab
                small
                class="mx-2"
                v-bind="attrs"
                v-on="on"
                color="orange"
                @click="helpDialog = true"
              >
                <v-icon small>fa-question</v-icon>
              </v-btn>
            </template>
            <span>{{ $vuetify.lang.translator("showHelp") }}</span>
          </v-tooltip>
        </v-col>
      </v-row>
      <v-container>
        <v-form ref="form" v-model="valid" @submit.prevent="tryChangeProfile">
          <v-row>
            <v-col cols="8">
              <v-text-field
                v-model="name"
                :label="$vuetify.lang.translator('name') + ' *'"
                :rules="nameRules"
                ref="focusField"
                autofocus
                clearable
              ></v-text-field>
            </v-col>
            <v-col cols="4">
              <v-select
                v-model="language"
                :label="$vuetify.lang.translator('language')"
                :items="allLanguages"
                item-text="name"
                item-value="id"
              ></v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="password"
                :rules="passwordRules"
                :label="$vuetify.lang.translator('password')"
                :type="showPassword ? 'text' : 'password'"
                :append-icon="showPassword ? 'fa-eye' : 'fa-eye-slash'"
                @click:append="showPassword = !showPassword"
                autocomplete="on"
                clearable
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model="newPassword"
                :rules="newPasswordRules"
                :label="$vuetify.lang.translator('newPassword')"
                :type="showNewPassword ? 'text' : 'password'"
                :append-icon="showNewPassword ? 'fa-eye' : 'fa-eye-slash'"
                @click:append="showNewPassword = !showNewPassword"
                autocomplete="on"
                clearable
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model="confirmPassword"
                :rules="confirmPasswordRules"
                :label="$vuetify.lang.translator('confirmNewPassword')"
                :type="showConfirmPassword ? 'text' : 'password'"
                :append-icon="showConfirmPassword ? 'fa-eye' : 'fa-eye-slash'"
                @click:append="showConfirmPassword = !showConfirmPassword"
                autocomplete="on"
                clearable
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-btn
                color="primary"
                :loading="loading"
                :disabled="loading"
                type="submit"
              >
                <v-icon class="mx-2">fa-save</v-icon
                >{{ $vuetify.lang.translator("change") }}
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-container>
    </v-card>

    <v-dialog
      v-model="helpDialog"
      persistent
      max-width="800"
      @keydown.esc="helpDialog = false"
      transition="dialog-top-transition"
    >
      <v-card>
        <v-toolbar dense elevation="2">
          <v-toolbar-title>{{
            $vuetify.lang.translator("help")
          }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn icon @click="helpDialog = false">
              <v-icon>fa-times</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-card-text>
          <div
            class="mt-6"
            v-html="$vuetify.lang.translator('profileHelp')"
          ></div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn color="success" @click="helpDialog = false">{{
            $vuetify.lang.translator("gotIt")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { required, min, max, sameValue } from "@/helpers/validations";
import { handleError } from "@/helpers/helper";

export default {
  name: "Dashboard",
  data: () => ({
    breadcrumbsItems: [],
    valid: false,
    showPassword: false,
    showNewPassword: false,
    showConfirmPassword: false,
    name: "",
    language: "",
    password: "",
    newPassword: "",
    confirmPassword: "",
    nameRules: [],
    passwordRules: [],
    newPasswordRules: [],
    confirmPasswordRules: [],
    allLanguages: [
      { id: "fa", name: "فارسی" },
      { id: "en", name: "English" }
    ],
    loading: false,
    helpDialog: false
  }),
  created() {
    this.name = this.$store.getters.NAME;
    this.language = this.$store.getters.LANGUAGE;

    this.breadcrumbsItems.push({
      text: this.$vuetify.lang.translator("dashboard"),
      to: { name: "Dashboard" }
    });
    this.breadcrumbsItems.push({
      text: this.$vuetify.lang.translator("profile"),
      to: { name: "Profile" },
      exact: true
    });
  },
  methods: {
    tryChangeProfile() {
      this.nameRules = [
        required(this.$vuetify.lang.translator("name"), this.$vuetify),
        max(100, this.$vuetify.lang.translator("name"), this.$vuetify)
      ];
      if (this.password || this.newPassword || this.confirmPassword) {
        this.passwordRules = [
          min(6, this.$vuetify.lang.translator("password"), this.$vuetify),
          max(30, this.$vuetify.lang.translator("password"), this.$vuetify)
        ];
        this.newPasswordRules = [
          min(6, this.$vuetify.lang.translator("newPassword"), this.$vuetify),
          max(30, this.$vuetify.lang.translator("newPassword"), this.$vuetify)
        ];
        this.confirmPasswordRules = [
          min(
            6,
            this.$vuetify.lang.translator("confirmPassword"),
            this.$vuetify
          ),
          max(
            30,
            this.$vuetify.lang.translator("confirmPassword"),
            this.$vuetify
          ),
          sameValue(
            this.$vuetify.lang.translator("confirmPassword"),
            this.$vuetify.lang.translator("newPassword"),
            this.newPassword,
            this.$vuetify
          )
        ];
      }
      var fields = { name: this.name, language: this.language };
      if (this.password && this.newPassword && this.confirmPassword) {
        fields.password = this.password;
        fields.new_password = this.newPassword;
        fields.confirm_password = this.confirmPassword;
      }
      let self = this;
      setTimeout(function() {
        if (self.$refs.form.validate()) {
          self.loading = true;
          self.$http
            .post("/user/profile/", fields)
            .then(() => {
              self.$toasted.success(
                self.$vuetify.lang.translator("profileSaved"),
                {
                  duration: 5000,
                  keepOnHover: true,
                  className:
                    self.$store.getters.LANGUAGE == "fa"
                      ? "vazir-font-fd"
                      : "vazir-font",
                  iconPack: "fontawesome",
                  icon: "check",
                  position: "top-center"
                }
              );
              self.resetData();
              self.$refs.focusField.focus();
            })
            .catch(error => {
              let result = handleError(
                error,
                self.$toasted,
                self.$router,
                self.$store
              );
              for (var item in result) {
                self.$toasted.error(result[item], {
                  duration: 5000,
                  keepOnHover: true,
                  className:
                    self.$store.getters.LANGUAGE == "fa"
                      ? "vazir-font-fd"
                      : "vazir-font",
                  iconPack: "fontawesome",
                  icon: "exclamation-triangle",
                  position: "top-center"
                });
              }
            })
            .then(function() {
              self.loading = false;
            });
        }
      });
    },
    resetData() {
      this.nameRules = [];
      this.passwordRules = [];
      this.newPasswordRules = [];
      this.confirmPasswordRules = [];

      this.password = "";
      this.newPassword = "";
      this.confirmPassword = "";
    }
  }
};
</script>
